<template>
  <div class="container">
    <p class="text-muted mb-4" style="font-size: 14px;">
      Choose how students are identified and ordered in each listing area.
      <i
        v-if="!isEditable"
        style="font-size: 16px !important; color: #007bff; cursor: pointer;"
        class="far fa-edit ml-2"
        @click="isEditable = true"
      ></i>
    </p>

    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" />
    </div>

    <div v-else>
      <!-- Student Listing Order Table -->
      <table class="table table-bordered table-sm">
        <thead class="thead-dark">
          <tr>
            <th style="width: 40%;">Listing Area</th>
            <th class="text-center">Roll Number</th>
            <th class="text-center">Admission Number</th>
            <th class="text-center">Enrollment Number</th>
            <th class="text-center">Register Number</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="area in areas" :key="area.key">
            <td class="align-middle font-weight-bold">{{ area.label }}</td>
            <td class="text-center align-middle">
              <b-form-radio
                v-model="settings[area.key]"
                :value="'roll_number'"
                :name="area.key"
                :disabled="!isEditable"
              />
            </td>
            <td class="text-center align-middle">
              <b-form-radio
                v-model="settings[area.key]"
                :value="'admission_number'"
                :name="area.key"
                :disabled="!isEditable"
              />
            </td>
            <td class="text-center align-middle">
              <b-form-radio
                v-model="settings[area.key]"
                :value="'enrollment_no'"
                :name="area.key"
                :disabled="!isEditable"
              />
            </td>
            <td class="text-center align-middle">
              <b-form-radio
                v-model="settings[area.key]"
                :value="'register_number'"
                :name="area.key"
                :disabled="!isEditable"
              />
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Class Reports Configuration Table -->
      <p class="text-muted mt-4 mb-2" style="font-size: 14px;">
        Configure which fields are displayed and how results are sorted in class reports.
      </p>
      <table class="table table-bordered table-sm">
        <thead class="thead-dark">
          <tr>
            <th style="width: 30%;">Class Reports</th>
            <th class="text-center">Roll Number</th>
            <th class="text-center">Admission Number</th>
            <th class="text-center">Enrollment Number</th>
            <th class="text-center">Register Number</th>
            <th class="text-center" style="width: 15%;">Sort By</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="report in reports" :key="report.key">
            <td class="align-middle font-weight-bold">{{ report.label }}</td>
            <td class="text-center align-middle" v-for="field in reportFields" :key="field.key">
              <b-form-checkbox
                v-model="reportSettings[report.key].display"
                :value="field.key"
                :disabled="!isEditable"
              />
            </td>
            <td class="text-center align-middle">
              <b-form-select
                v-model="reportSettings[report.key].sort_by"
                :options="sortByOptions"
                :disabled="!isEditable"
                size="sm"
              />
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Course Reports Configuration Table -->
      <p class="text-muted mt-4 mb-2" style="font-size: 14px;">
        Configure which fields are displayed and how results are sorted in course reports.
      </p>
      <table class="table table-bordered table-sm">
        <thead class="thead-dark">
          <tr>
            <th style="width: 30%;">Course Reports</th>
            <th class="text-center">Roll Number</th>
            <th class="text-center">Admission Number</th>
            <th class="text-center">Enrollment Number</th>
            <th class="text-center">Register Number</th>
            <th class="text-center" style="width: 15%;">Sort By</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="report in courseReportsList" :key="report.key">
            <td class="align-middle font-weight-bold">{{ report.label }}</td>
            <td class="text-center align-middle" v-for="field in reportFields" :key="field.key">
              <b-form-checkbox
                v-model="courseReportSettings[report.key].display"
                :value="field.key"
                :disabled="!isEditable"
              />
            </td>
            <td class="text-center align-middle">
              <b-form-select
                v-model="courseReportSettings[report.key].sort_by"
                :options="sortByOptions"
                :disabled="!isEditable"
                size="sm"
              />
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Activity Reports Configuration Table -->
      <p class="text-muted mt-4 mb-2" style="font-size: 14px;">
        Configure which fields are displayed and how results are sorted in activity reports.
      </p>
      <table class="table table-bordered table-sm">
        <thead class="thead-dark">
          <tr>
            <th style="width: 30%;">Activity Reports</th>
            <th class="text-center">Roll Number</th>
            <th class="text-center">Admission Number</th>
            <th class="text-center">Enrollment Number</th>
            <th class="text-center">Register Number</th>
            <th class="text-center" style="width: 15%;">Sort By</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="report in activityReportsList" :key="report.key">
            <td class="align-middle font-weight-bold">{{ report.label }}</td>
            <td class="text-center align-middle" v-for="field in reportFields" :key="field.key">
              <b-form-checkbox
                v-model="activityReportSettings[report.key].display"
                :value="field.key"
                :disabled="!isEditable"
              />
            </td>
            <td class="text-center align-middle">
              <b-form-select
                v-model="activityReportSettings[report.key].sort_by"
                :options="sortByOptions"
                :disabled="!isEditable"
                size="sm"
              />
            </td>
          </tr>
        </tbody>
      </table>

      <b-row class="mt-3" v-if="isEditable">
        <b-col class="text-right">
          <b-button variant="primary" :disabled="saving" @click="saveSettings">
            <b-spinner small v-if="saving" class="mr-1" />
            {{ saving ? 'Saving...' : 'Save' }}
          </b-button>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
const AREAS = [
  { key: 'student_list_order',   label: 'Student List Order' },
  { key: 'attendance',     label: 'Attendance' },
  // { key: 'activity',     label: 'Activity' },
  { key: 'assignment',     label: 'Assignment' },
  { key: 'assesment',     label: 'Assesment' },
  { key: 'lab',         label: 'Laboratary' },
  { key: 'project',         label: 'Project with CO-Mapping' },
  { key: 'seminar',         label: 'Seminar with CO-Mapping' },
  { key: 'end_semester_exam',         label: 'End Semester Exam' },
  // { key: 'reports',        label: 'Reports' },
  // { key: 'certificate',    label: 'Certificate' },
  // { key: 'mark_entry',     label: 'Mark Entry' },
];

const REPORTS = [
  { key: 'student_roll_list',           label: 'Student Roll List' },
  { key: 'attendance_student_vs_date',  label: 'Students Attendance Report (Student vs Date)' },
  { key: 'attendance_student_vs_date_session',     label: 'Students Attendance Report (Student vs Date, Session, Course)' },
  { key: 'consolidated_statement_of_course_attendance',       label: 'Consolidated Statement of Course Attendance' },
  { key: 'statement_of_assessment_marks',                     label: 'Statement of Assessment Marks With Result Analysis' },
  { key: 'consolidated_statement_of_internal_marks',          label: 'Consolidated Statement of Internal Marks' },
  { key: 'university_exams_result_analysis',                  label: 'University Exams Result Analysis' },
];

const COURSE_REPORTS = [
  { key: 'student_roll_list',            label: 'Student Roll List' },
  { key: 'attendance_detailed',          label: 'Attendance Report (Detailed)' },
  { key: 'attendance_consolidated',      label: 'Attendance Report (Consolidated)' },
  { key: 'lab_evaluation_sheet',         label: 'Lab Evaluation Sheet' },
  { key: 'project_evaluation_sheet',     label: 'Project Evaluation Sheet' },
  { key: 'seminar_evaluation_sheet',     label: 'Seminar Evaluation Sheet' },
  { key: 'internal_evaluation_report',   label: 'Internal Evaluation Report' },
  { key: 'attainment_report',            label: 'Attainment Report' },
];

const ACTIVITY_REPORTS = [
  { key: 'activity_question_wise',    label: 'Question wise mark with CO mapping' },
  { key: 'activity_result_analysis',  label: 'Result Analysis' },
];

const REPORT_FIELDS = [
  { key: 'roll_number',     label: 'Roll Number' },
  { key: 'admission_number', label: 'Admission Number' },
  { key: 'enrollment_no',   label: 'Enrollment Number' },
  { key: 'register_number', label: 'Register Number' },
];

const DEFAULT_ORDER = 'roll_number';

const defaultReportSetting = () => ({ display: ['roll_number'], sort_by: DEFAULT_ORDER });

export default {
  props: ['usergroup_name', 'usergroup_id', 'group_id', 'usersubgroup_id'],

  async created() {
    await this.loadSettings();
  },

  data() {
    const defaultSettings = {};
    AREAS.forEach(a => { defaultSettings[a.key] = DEFAULT_ORDER; });

    const defaultReportSettings = {};
    REPORTS.forEach(r => { defaultReportSettings[r.key] = defaultReportSetting(); });

    const defaultCourseReportSettings = {};
    COURSE_REPORTS.forEach(r => { defaultCourseReportSettings[r.key] = defaultReportSetting(); });

    const defaultActivityReportSettings = {};
    ACTIVITY_REPORTS.forEach(r => { defaultActivityReportSettings[r.key] = defaultReportSetting(); });

    return {
      areas: AREAS,
      reports: REPORTS,
      courseReportsList: COURSE_REPORTS,
      activityReportsList: ACTIVITY_REPORTS,
      reportFields: REPORT_FIELDS,
      settings: { ...defaultSettings },
      reportSettings: defaultReportSettings,
      courseReportSettings: defaultCourseReportSettings,
      activityReportSettings: defaultActivityReportSettings,
      sortByOptions: [
        { value: 'roll_number',     text: 'Roll Number' },
        { value: 'admission_number', text: 'Admission Number' },
        { value: 'enrollment_no',   text: 'Enrollment Number' },
        { value: 'register_number', text: 'Register Number' },
      ],
      loading: true,
      saving: false,
      isEditable: false,
    };
  },

  methods: {
    async loadSettings() {
      this.loading = true;
      const baseUrl = `${this.$store.getters.getAPIKey.mainAPI}/usergroups-configuration-groups/setting/${this.group_id}`;
      try {
        const [orderRes, reportRes, courseReportRes, activityReportRes] = await Promise.all([
          this.$axios.get(`${baseUrl}/student_listing_order`),
          this.$axios.get(`${baseUrl}/class_report_config`),
          this.$axios.get(`${baseUrl}/course_report_config`),
          this.$axios.get(`${baseUrl}/activity_report_config`),
        ]);
        if (orderRes.data && typeof orderRes.data === 'object') {
          this.settings = { ...this.settings, ...orderRes.data };
        }
        if (reportRes.data && typeof reportRes.data === 'object') {
          REPORTS.forEach(r => {
            if (reportRes.data[r.key]) {
              this.reportSettings[r.key] = { ...defaultReportSetting(), ...reportRes.data[r.key] };
            }
          });
        }
        if (courseReportRes.data && typeof courseReportRes.data === 'object') {
          COURSE_REPORTS.forEach(r => {
            if (courseReportRes.data[r.key]) {
              this.courseReportSettings[r.key] = { ...defaultReportSetting(), ...courseReportRes.data[r.key] };
            }
          });
        }
        if (activityReportRes.data && typeof activityReportRes.data === 'object') {
          ACTIVITY_REPORTS.forEach(r => {
            if (activityReportRes.data[r.key]) {
              this.activityReportSettings[r.key] = { ...defaultReportSetting(), ...activityReportRes.data[r.key] };
            }
          });
        }
      } catch {
        // no saved settings yet — defaults apply
      } finally {
        this.loading = false;
      }
    },

    async saveSettings() {
      this.saving = true;
      const baseUrl = `${this.$store.getters.getAPIKey.mainAPI}/usergroups-configuration-groups/setting/${this.group_id}`;
      try {
        await this.$axios.post(`${baseUrl}/student_listing_order`, { student_listing_order: this.settings });
        await this.$axios.post(`${baseUrl}/class_report_config`, { class_report_config: this.reportSettings });
        await this.$axios.post(`${baseUrl}/course_report_config`, { course_report_config: this.courseReportSettings });
        await this.$axios.post(`${baseUrl}/activity_report_config`, { activity_report_config: this.activityReportSettings });
        this.$toast.success('Settings saved.', { position: 'top', duration: 3000 });
        this.isEditable = false;
      } catch (e) {
        this.$toast.error(e.response?.data?.message || 'Failed to save settings.', { position: 'top', duration: 5000 });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.container {
  max-width: 100%;
  padding: 20px;
}
.subgroupname {
  font-weight: bold;
  text-align: center;
  color: #007bff;
  text-transform: uppercase;
}
thead th {
  font-size: 13px;
}
tbody td {
  font-size: 14px;
}
</style>
